import React, { useEffect, useState } from 'react';
import { Eye, DownloadSimple, SpinnerGap } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { downloadAdhesionKitPdf, getAdminAdhesionKitPdf } from '../../services/api';

export const KitPdfActions = ({ kitId, token, publicAccess = false, documentRequest, documentFilename, scopeId }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const scope = scopeId || (publicAccess ? 'public' : kitId);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const openPdf = async (download) => {
    setBusy(download ? 'download' : 'preview');
    setError('');
    try {
      const response = documentRequest ? await documentRequest(download) : publicAccess
        ? await downloadAdhesionKitPdf(token, download)
        : await getAdminAdhesionKitPdf(kitId, download);
      if (!(await response.data.slice(0, 5).text()).startsWith('%PDF-')) throw new Error('Le document reçu n’est pas un PDF valide.');
      const url = URL.createObjectURL(response.data);
      if (download) {
        const link = document.createElement('a');
        link.href = url;
        link.download = documentFilename || `kit-adhesion-${kitId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else setPreview(url);
    } catch (e) {
      let message = 'Impossible de charger le kit PDF. Réessayez dans quelques instants.';
      try { message = JSON.parse(await e.response.data.text()).detail || message; } catch {}
      setError(message);
    } finally { setBusy(null); }
  };

  return <>
    <div className="flex flex-wrap items-center gap-1" data-testid={`kit-pdf-actions-${scope}`}>
      <Button size="sm" variant={publicAccess ? 'outline' : 'ghost'} disabled={!!busy} onClick={() => openPdf(false)} title="Visualiser le kit PDF" aria-label="Visualiser le kit PDF" data-testid={`view-kit-pdf-${scope}`}>
        {busy === 'preview' ? <SpinnerGap size={16} className="animate-spin" /> : <Eye size={16} />}
        {publicAccess && <span className="ml-2">Visualiser le kit</span>}
      </Button>
      <Button size="sm" variant={publicAccess ? 'outline' : 'ghost'} disabled={!!busy} onClick={() => openPdf(true)} title="Télécharger le kit PDF" aria-label="Télécharger le kit PDF" data-testid={`download-kit-pdf-${scope}`}>
        {busy === 'download' ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
        {publicAccess && <span className="ml-2">Télécharger le PDF</span>}
      </Button>
    </div>
    {error && <p role="alert" className="basis-full text-xs text-red-700 max-w-sm" data-testid={`kit-pdf-error-${scope}`}>{error}</p>}
    <Dialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
      <DialogContent className="w-[calc(100%_-_2rem)] max-w-5xl" data-testid={`kit-pdf-preview-${scope}`}>
        <DialogHeader>
          <DialogTitle data-testid={`kit-pdf-preview-title-${scope}`}>Kit d'adhésion</DialogTitle>
          <DialogDescription>Document PDF</DialogDescription>
        </DialogHeader>
        {preview && <iframe src={preview} title="Aperçu du kit d'adhésion" className="w-full h-[65vh] border rounded" data-testid={`kit-pdf-frame-${scope}`} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => setPreview(null)} data-testid={`kit-pdf-close-${scope}`}>Fermer</Button>
          <Button disabled={!!busy} onClick={() => openPdf(true)} data-testid={`kit-pdf-preview-download-${scope}`}><DownloadSimple size={16} className="mr-2" /> Télécharger</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
};